/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { withMyTheme, DESKTOP_CONTENT_PADDING, DESKTOP_TITLE_FONT_SIZE, MOBILE_CONTENT_PADDING, MOBILE_TITLE_FONT_SIZE, SMALL_ROUNDED_CORNER } from "../../theme/theme";
import { mobileCss, isMobile } from "../../theme/isMobile";
import { Image } from '../../Images';
import { useInView } from 'react-intersection-observer';

export const STUDIO_ID = 'studio';

const StudioContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    max-height: 90vh;
    ${DESKTOP_CONTENT_PADDING}
    color: ${theme.palette.text.primary};
    background-color: ${theme.palette.background.default};
    gap: 10vw;

    ${mobileCss(`
        max-width: 100vw;
        flex-direction: column;
        justify-content: center;
        ${MOBILE_CONTENT_PADDING}
        gap: 2vh;
    `)}
`);

const ImageContainerStyle = withMyTheme((theme, isVisible: boolean = false) => css`
    height: auto;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    max-height: 80vh;
    transform: translateX(${isVisible ? '0' : '-50px'});
    opacity: ${isVisible ? 1 : 0};
    transition: transform 0.8s ease-out, opacity 0.8s ease-out;

    ${mobileCss(`
        flex-direction: column;
        height: auto;
        align-items: center;
        width: 100vw;
        order: 2;
    `)}
`);

const StudioImageStyle = css`
    height: auto;
    width: 45vw;
    aspect-ratio: 1600 / 918;
    object-fit: cover;
    object-position: center 55%;
    display: block;
    border-radius: 12px;
    ${mobileCss(`
        width: 90vw;
        height: auto;
        aspect-ratio: 1600 / 2133;
        object-position: center;
    `)}
`;

const ContentContainerStyle = withMyTheme((theme, isVisible: boolean = false) => css`
    width: 35%;
    display: flex;
    flex-direction: column;
    text-align: left;
    align-items: flex-start;
    justify-content: center;
    transform: translateX(${isVisible ? '0' : '50px'});
    opacity: ${isVisible ? 1 : 0};
    transition: transform 0.8s ease-out, opacity 0.8s ease-out;

    ${mobileCss(`
        order: 1;
        align-items: center;
        text-align: center;
    `)}
`);

const TitleStyle = withMyTheme((theme) => css`
    font-size: ${DESKTOP_TITLE_FONT_SIZE};
    font-family: ${theme.typography.h1.fontFamily};
    color: ${theme.palette.primary.main};
    margin-bottom: 1vh;
    margin-top: 0;
    
    ${mobileCss(`
        font-size: ${MOBILE_TITLE_FONT_SIZE};
        margin-bottom: 2vh;
    `)}
`);

const AddressStyle = withMyTheme((theme) => css`
    font-size: 1.2rem;
    line-height: 1.6;
    font-family: ${theme.typography.body1.fontFamily};
    
    ${mobileCss(`
        font-size: 1rem;
    `)}
`);

const LinkStyle = withMyTheme((theme) => css`
    color: ${theme.palette.primary.dark};
    text-decoration: none;
    font-weight: 600;
    transition: color 0.2s ease-in-out;
    
    &:hover {
        color: ${theme.palette.primary.main};
        text-decoration: underline;
    }
`);

export const Studio = () => {
    const { t } = useTranslation();
    const { ref, inView } = useInView({
        threshold: 0.15,
        triggerOnce: true,
        delay: 200
    });

    return (
        <section css={StudioContainerStyle} id={STUDIO_ID} ref={ref}>
            <div css={(theme) => ImageContainerStyle(theme, inView)}>
                <img
                    src={Image.STUDIO_TILES}
                    alt="Makeup Studio"
                    width={1600}
                    height={2133}
                    css={StudioImageStyle}
                />
            </div>
            <div css={(theme) => ContentContainerStyle(theme, inView)}>
                <h2 css={TitleStyle}>Studio</h2>
                <p css={AddressStyle}>{t('footer.address')}</p>
                <a 
                    href="https://maps.app.goo.gl/6o3WMwz2J1sVAk4ZA" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    css={LinkStyle}
                >
                    Zobacz na mapie
                </a>
            </div>
        </section>
    );
};